/**
 * 日程「打开其它 App」：从孩子的安卓平板上选一个能打开的 App（只列有桌面图标的，与 iOS DeviceAppPickerSheet 一致）。
 * 孩子有几台安卓平板时上面切换；点一个 App 就选好并关闭。实时通道 device_apps_changed 是当前平板时重新读取。
 */
import { ChevronRight, Search, TabletSmartphone, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, fetchDeviceApps, filterApps, isAndroidTablet, launchableApps, onRealtime, select, shallowEqual, useStore, type DeviceApp } from "../../core";
import { AppRow, AppSearchField } from "../../components/DeviceApps";
import { Button, ChoiceChip, C, EmptyHint, RowDivider, Sheet, SheetBody, SheetHeader, Spinner } from "../../ui";

/** 孩子的安卓平板（没解绑的） */
export function useAndroidTablets(childId: string) {
  return useStore((s) => select.studentDevices(s, childId).filter(isAndroidTablet), shallowEqual);
}

export function AppPickerSheet({ open, childId, onClose, onPick }: { open: boolean; childId: string; onClose: () => void; onPick: (app: DeviceApp) => void }) {
  return (
    <Sheet open={open} onClose={onClose} width={520} label="选择平板上的 App">
      {open && <PickerBody childId={childId} onClose={onClose} onPick={onPick} />}
    </Sheet>
  );
}

function PickerBody({ childId, onClose, onPick }: { childId: string; onClose: () => void; onPick: (app: DeviceApp) => void }) {
  const tablets = useAndroidTablets(childId);
  const [picked, setPicked] = useState<string | null>(null);
  const deviceId = picked && tablets.some((d) => d.id === picked) ? picked : (tablets[0]?.id ?? "");
  /** 每台平板读到的 App（切换平板时不重复读取） */
  const [apps, setApps] = useState<Record<string, DeviceApp[]>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [query, setQuery] = useState("");
  const loadSeq = useRef<Record<string, number>>({});

  const load = useCallback(async (id: string) => {
    if (!id) return;
    const my = (loadSeq.current[id] = (loadSeq.current[id] ?? 0) + 1);
    try {
      const r = await fetchDeviceApps(id);
      if (my !== loadSeq.current[id]) return;
      setApps((m) => ({ ...m, [id]: r.apps }));
      setErrors(({ [id]: _, ...rest }) => rest);
    } catch (e) {
      if (my !== loadSeq.current[id] || (e instanceof ApiError && e.isUnauthorized)) return;
      setErrors((m) => ({ ...m, [id]: e instanceof Error ? e.message : String(e) }));
    }
  }, []);

  const loaded = apps[deviceId];
  useEffect(() => {
    if (deviceId && !loaded) void load(deviceId);
  }, [deviceId, loaded, load]);

  useEffect(
    () =>
      onRealtime("device_apps_changed", (m) => {
        if (m.deviceId === deviceId) void load(deviceId);
      }),
    [deviceId, load],
  );

  const launchable = useMemo(() => launchableApps(loaded ?? []), [loaded]);
  const shown = useMemo(() => filterApps(launchable, query), [launchable, query]);
  const error = errors[deviceId];

  let list;
  if (!deviceId) {
    list = <EmptyHint icon={TabletSmartphone} text="这个孩子还没有绑定安卓平板" />;
  } else if (!loaded) {
    list = error ? (
      <EmptyHint icon={TriangleAlert} text={error}>
        <Button variant="soft" height={40} onClick={() => void load(deviceId)}>
          重试
        </Button>
      </EmptyHint>
    ) : (
      <div className="sch-pick-loading">
        <Spinner size={24} />
      </div>
    );
  } else if (launchable.length === 0) {
    list = <EmptyHint icon={TabletSmartphone} text="还没有上报，平板打开学生端后会自动上报" />;
  } else if (shown.length === 0) {
    list = <EmptyHint icon={Search} text={`没有找到「${query.trim()}」`} />;
  } else {
    list = (
      <div className="sch-pick-card">
        {shown.map((a, i) => (
          <div key={a.package}>
            {i > 0 && <RowDivider />}
            <button
              type="button"
              className="sch-pick-row"
              onClick={() => {
                onPick(a);
                onClose();
              }}
            >
              <AppRow app={a} subtitle={a.package} trailing={<ChevronRight size={16} strokeWidth={2.4} color={C.inkMuted} />} />
            </button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <SheetHeader title="选择平板上的 App" onClose={onClose} />
      <SheetBody gap={10}>
        {tablets.length > 1 && (
          <div className="sch-ed-chips" role="group" aria-label="平板">
            {tablets.map((d) => (
              <ChoiceChip key={d.id} selected={d.id === deviceId} unselectedBg={C.surface} onClick={() => setPicked(d.id)}>
                {d.name}
              </ChoiceChip>
            ))}
          </div>
        )}
        {deviceId && <AppSearchField value={query} onChange={setQuery} />}
        {list}
      </SheetBody>
    </>
  );
}
